import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CLIENT_ID_PATTERN } from '@/db/types/client';

// Pre-compiled route patterns for offline-capable routes
const OFFLINE_ROUTE_PATTERNS = [
  new RegExp(`^/clients/${CLIENT_ID_PATTERN}$`),
  new RegExp(`^/clients/${CLIENT_ID_PATTERN}/documents/\\d+$`),
  new RegExp(`^/clients/${CLIENT_ID_PATTERN}/documents/\\d+/edit$`),
  new RegExp(`^/clients/${CLIENT_ID_PATTERN}/documents/drafts/[^/]+/edit$`),
  // Legacy assessment routes for backward compatibility
  new RegExp(`^/clients/${CLIENT_ID_PATTERN}/assessments/\\d+$`),
  new RegExp(`^/clients/${CLIENT_ID_PATTERN}/assessments/\\d+/edit$`),
  /^\/assessments\/\d+$/,
  /^\/assessments\/\d+\/edit$/,
  /^\/clients$/,
  /^\/$/,
];

/**
 * Client-side routing configuration for offline-first functionality
 * Ensures all navigation happens on the client side with proper IndexedDB integration
 */
export default function ClientRouterConfig() {
  const navigate = useNavigate();

  useEffect(() => {
    // Override default link behavior to ensure client-side routing
    const handleLinkClick = (e: Event) => {
      // Skip if already handled (e.g. by React Router's <Link>)
      if (e.defaultPrevented) return;

      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;

      if (link?.href) {
        const url = new URL(link.href);

        // Only handle same-origin links
        if (url.origin === window.location.origin) {
          const isOfflineRoute = OFFLINE_ROUTE_PATTERNS.some(pattern => pattern.test(url.pathname));

          if (isOfflineRoute) {
            e.preventDefault();
            navigate(url.pathname + url.search);
          }
        }
      }
    };

    // Add click handler to document
    document.addEventListener('click', handleLinkClick);

    // Service worker messaging for route handling
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.ready.then(registration => {
        // Send message to service worker about available routes
        registration.active?.postMessage({
          type: 'ROUTE_CONFIG',
          routes: [
            '/clients',
            '/app-shell',
          ]
        });
      });
    }

    return () => {
      document.removeEventListener('click', handleLinkClick);
    };
  }, [navigate]);

  return null; // This component doesn't render anything
}
